import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { isMainland } from '@shakkerai/pro-utils';
import { applyModel } from '../../helpers';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { getProjectState, setTempState } from '@starflow/app/store';
import { getUnitData, updateUnitData } from '@starflow/app/helpers/flow';
import { communityUrl } from '../../constants';
import { useModelFavorite, useModelDetail, useModelVersionIntroDetail } from '../../hooks/data';
import crownIcon from '../../assets/crown_outlined.png';
import { libVipIcon } from '@shakkerai/pro-commercial';

export const LoraDetailWindow = React.memo((props) => {
  const { data } = props;
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);

  const { loading, modelDetail } = useModelDetail(data.uuid);
  const { favorited, favoriteModel } = useModelFavorite(modelDetail);
  const versionIntro = useModelVersionIntroDetail(modelDetail?.versionIntro);

  const handleApplyBaseModel = useCallback((model) => {
    applyModel({
      ...model,
      remark: null,
      baseType: model.baseType,
      modelType: 'checkpoint',
    });
  }, []);

  const handleApplyTriggerWord = useCallback((text) => {
    const unitId = getProjectState('currentNodeId');
    const unitData = getUnitData(unitId);

    if (!unitData) return;

    const originPrompt = unitData?.prompt;
    const promptPrefix = originPrompt ? originPrompt + ',' : '';
    const start = promptPrefix.length;
    const end = start + text.length;

    const value = `${promptPrefix}${text}`;
    updateUnitData(null, { prompt: value });

    setTimeout(() => {
      let promptDom = document.getElementById(`prompt-input-${unitId}`);

      if (!promptDom) return;

      if (promptDom.tagName !== 'TEXTAREA') {
        promptDom = promptDom.querySelector('textarea');
      }

      if (promptDom) {
        promptDom.selectionStart = start;
        promptDom.selectionEnd = end;
        promptDom.focus();

        setTimeout(() => {
          if (promptDom) {
            promptDom.selectionStart = 0;
            promptDom.selectionEnd = 0;
            promptDom.focus();
          }
        }, 2000);
      }
    }, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleFavorite = useCallback(() => {
    favoriteModel({
      type: favorited ? 0 : 1,
      source: 'detail',
    });
  }, [favorited, favoriteModel]);

  const handleViewModel = useCallback(() => {
    window.open(`${communityUrl}/modelinfo/${modelDetail.modelUuid}`);
  }, [modelDetail?.modelUuid]);

  const handleApplyParams = useCallback(() => {
    if (versionIntro) {
      updateUnitData(null, (params) => {
        const updatedParams = {};

        if (versionIntro?.cfg) {
          updatedParams.cfgScale = Number(versionIntro.cfg);
        }

        if (versionIntro?.vae && versionIntro?.vae !== '"none"') {
          updatedParams.vae = versionIntro.vae;
        }

        if (versionIntro?.weight) {
          updatedParams.loraModels = params.loraModels.map((item) => {
            if (item.uuid !== data.uuid) return item;

            return { ...item, weight: versionIntro.weight };
          });
        }

        return updatedParams;
      });
    }
  }, [data.uuid, versionIntro]);

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="lora-detail"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    if (data?.versionId) {
      const ref = document.getElementById(`lora-detail-entry-${data?.versionId}`);
      const { x, y } = ref.getBoundingClientRect();

      setPosition({ x: x - 320, y });
    }
  }, [data]);

  const disabled = useMemo(() => {
    return versionIntro?.loading || !versionIntro || !versionIntro?.cfg;
  }, [versionIntro]);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd default={position} scale={1} className="z-[100]" dragHandleClassName="sub-drag-handle">
      <div ref={containerRef} className="w-[300px] bg-white shadow-light rounded-xs">
        <div className="sub-drag-handle flex items-center justify-between px-4 py-2 cursor-move border-b border-[#efefef]">
          <span className="text-mini font-semibold text-caption-500">{translate('lora_details_title')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </div>
        <div className="flex items-center p-4 border-b border-[#efefef]">
          <img src={convert2CompressedUrl(data.imageUrl, 100)} className="w-[50px] h-[66px] object-cover rounded" />
          <div className="flex flex-col ml-2">
            <span className="w-[200px] text-[#2d2d2d] font-grotesk font-semibold whitespace-nowrap text-ellipsis overflow-hidden text-sm">
              {data.name}
            </span>
            <div className="flex items-center mt-0.5 gap-2">
              <span className="text-xs font-grotesk text-[#7A7A7A]">{`${data.baseType}`.replace(/\s/g, '')}</span>
              {data.vipUsed === 1 && (
                <>
                  <div className="bg-[#EFEFEF] w-[1px] h-[10px]"></div>
                  <img src={isMainland ? libVipIcon : crownIcon} className="w-[13px] relative -top-[1.5px]" alt="" />
                </>
              )}
            </div>
          </div>
        </div>
        {versionIntro?.loading ? (
          <div className="h-4" />
        ) : (
          <div className="p-4 pb-2">
            <h5 className="mb-3 text-xs text-[#2d2d2d] font-semibold">{translate('lora_details_title2')}</h5>
            <div className="flex flex-wrap">
              {versionIntro?.relatedCheckpoints?.length ? (
                versionIntro?.relatedCheckpoints?.map((item) => (
                  <button
                    title={item.name}
                    key={item.versionId}
                    onClick={() => handleApplyBaseModel(item)}
                    className="flex items-center h-6 px-2 mr-2 mb-2 bg-[#F4F6F8] hover:bg-[#f1f2f3] rounded-xs"
                  >
                    <span className="max-w-[110px] text-[10px] text-[#2d2d2d] whitespace-nowrap text-ellipsis overflow-hidden">
                      {item.name}
                    </span>
                    <Icon className="ml-1 icon-border_change" fontSize={10} color="#666" />
                  </button>
                ))
              ) : (
                <span className="text-xs text-[#7f7f7f]">N/A</span>
              )}
            </div>
            <h5 className="mt-2 mb-3 text-xs text-[#2d2d2d] font-semibold">{translate('model_trigger')}</h5>
            <div className="flex flex-wrap">
              {versionIntro?.triggerWord?.length ? (
                versionIntro?.triggerWord?.map((item) => (
                  <button
                    title={item}
                    key={item}
                    onClick={() => handleApplyTriggerWord(item)}
                    className="flex items-center h-6 px-2 mr-2 mb-2 bg-[#F4F6F8] hover:bg-[#f1f2f3] rounded-xs"
                  >
                    <span className="max-w-[110px] text-[10px] text-[#2d2d2d] whitespace-nowrap text-ellipsis overflow-hidden">
                      {item}
                    </span>
                    <Icon className="ml-1 icon-border_increase" fontSize={10} color="#666" />
                  </button>
                ))
              ) : (
                <span className="text-xs text-[#7f7f7f]">N/A</span>
              )}
            </div>
          </div>
        )}
        {loading ? null : (
          <footer className="flex items-center justify-between p-4 pt-0 space-x-3 text-[#2d2d2d] text-xs font-medium">
            <button
              onClick={handleToggleFavorite}
              className="flex items-center justify-center w-[48px] h-[38px] hover:bg-[#f8f9fa] border border-[#dcdcdc] rounded"
            >
              <Icon
                className={`${favorited ? 'icon-filled_star' : 'icon-border_star'} -translate-y-px`}
                color={favorited ? '#FFC351' : '#2d2d2d'}
                fontSize={14}
              />
            </button>
            <button
              onClick={handleViewModel}
              className="flex items-center justify-center w-[48px] h-[38px] hover:bg-[#f8f9fa] border border-[#dcdcdc] rounded"
            >
              <Icon className="icon-External_Link" fontSize={14} />
            </button>
            <button
              onClick={handleApplyParams}
              disabled={disabled}
              className={`flex items-center justify-center flex-1 h-[38px] hover:opacity-90 ${
                disabled ? 'bg-slate-50 text-[#999]' : 'bg-primary text-white'
              }  rounded `}
            >
              {translate('lora_details_presetpara')}
            </button>
          </footer>
        )}
      </div>
    </Rnd>,
    document.body,
  );
});
